/* ──────────────────────────────────────────────────────────────────────────
   Days and weeks for CB Stats, shared by the server and the page. Every day
   is a Melbourne calendar day and a week runs Monday to Sunday, as the weekly
   report did ("OCT.26 - NOV.1"). The numbers it was typed from matched
   cbhours.com read in Melbourne time to the minute (LILAH, Nov 3: 3h 39m).
   ────────────────────────────────────────────────────────────────────────── */

export const ZONE = "Australia/Melbourne";

/** How often the sweep looks, and so how many minutes one sample stands for. */
export const SLOT_MINUTES = 3;

const dayFormat = new Intl.DateTimeFormat("en-CA", { timeZone: ZONE, year: "numeric", month: "2-digit", day: "2-digit" });
const timeFormat = new Intl.DateTimeFormat("en-AU", { timeZone: ZONE, hour: "numeric", minute: "2-digit", hour12: true });

/** The Melbourne day a moment falls on. */
export function melbourneDay(at: Date | string | number): string {
  return dayFormat.format(new Date(at));
}

/** "11:03am", in Melbourne. */
export function melbourneTime(at: Date | string | number): string {
  return timeFormat.format(new Date(at)).replace(/\s/g, "").toLowerCase();
}

export function addDays(day: string, n: number): string {
  const [y, m, d] = day.split("-").map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** 0 for Monday to 6 for Sunday. */
export function weekday(day: string): number {
  return (new Date(`${day}T12:00:00Z`).getUTCDay() + 6) % 7;
}

/** The Monday of the week a day is in. */
export function weekOf(day: string): string {
  return addDays(day, -weekday(day));
}

export function weekDays(monday: string): string[] {
  return Array.from({ length: 7 }, (_, i) => addDays(monday, i));
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** "Oct 26" */
export function shortDay(day: string): string {
  return `${MONTHS[Number(day.slice(5, 7)) - 1]} ${Number(day.slice(8, 10))}`;
}

/** "Tue, Nov 3" */
export function dayLabel(day: string): string {
  return `${DAYS[weekday(day)]}, ${shortDay(day)}`;
}

export const dayLetter = (day: string) => DAYS[weekday(day)]!;

/** "Oct 26 to Nov 1" */
export function weekLabel(monday: string): string {
  return `${shortDay(monday)} to ${shortDay(addDays(monday, 6))}`;
}

/** "3h 39m", "45m", "0m". */
export function duration(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = Math.round(minutes % 60);
  return h ? `${h}h ${String(m).padStart(2, "0")}m` : `${m}m`;
}
